<template>
    <div class="oddsBox show-option">
        <div
            v-bind:data-testid="`bet-builder-selection-${selection.id}`"
            v-bind:class="[
                'odds',
                'MatchCard_Btn_BetBuilderOdds',
                {
                    odds_rise: isPriceRise && !isSelectionLoading,
                    odds_drop: isPriceDrop && !isSelectionLoading,
                    odds_selected: isSelected,
                    odds_disabled: !isAvailable && !isSelected && !isSelectionLoading,
                    odds_locked: isSuspended,
                    'pointer-events-none': false,
                }]"
            v-on:click="clickSelection"
        >
            <div v-if="isSelectionLoading && !isSelected" class="skeletonWrapper animate-pulse">
                <span class="skeleton skeleton_dot" />
                <span class="skeleton skeleton_line" />
            </div>
            <div v-else-if="isSuspended" class="odds_locked">
                <SvgIcon iconName="systemIcon_Locked" />
            </div>
            <template v-else>
                <div v-if="isDisplayName" class="odds-label">
                    {{ name }}
                </div>
                <div v-if="isShowPrice || point" class="odds-wrapper">
                    <div class="odds-text">
                        <div v-if="point" class="odds-point">
                            {{ point }}
                        </div>
                        <div
                            v-if="isShowPrice"
                            class="odds-price"
                            v-bind:class="{ singleColumn: isSingleColumn }"
                        >
                            {{ displayPrice }}
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, toRefs } from 'vue';
    import { useBetBuilderSelection } from '@/components/betBuilder/useBetBuilderSelection';
    import type { IMatch } from '@/interface/IMatch';
    import type { IBetBuilderMarket, IBetBuilderSelection } from '@/interface/betBuilder';
    import { BetPageType, DisplaySection } from '@/interface/enum';
    import { useBetPage } from '@/libraries/composables/useBetPage';
    import { usePriceChange } from '@/libraries/composables/usePriceChange';
    import { platform } from '@/libraries/helpers/platformHelper';
    import { formatMinDecimalPlace } from '@/libraries/utils/text';
    import { useBetBuilderBetSlipStore } from '@/stores/betSlip/betBuilderBetSlipStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';
    import { useRootStore } from '@/stores/rootStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'Selection',
        components: {
            SvgIcon,
        },
        props: {
            market: {
                type: Object as PropType<IBetBuilderMarket>,
                required: true,
            },
            selection: {
                type: Object as PropType<IBetBuilderSelection>,
                required: true,
            },
            isDisplayName: {
                type: Boolean,
                default: true,
            },
            isDisplayShortName: {
                type: Boolean,
                default: false,
            },
            isSingleColumn: {
                type: Boolean,
                default: false,
            },
            match: {
                type: Object as PropType<IMatch>,
                default: () => ({}),
            },
            displaySection: {
                type: Number as PropType<DisplaySection>,
                default: DisplaySection.Unknown,
            },
        },
        setup(props) {
            const { market, selection, isDisplayShortName, match, displaySection } = toRefs(props);
            const {
                isSuspended,
                point,
                shortName,
                fullName,
            } = useBetBuilderSelection(market, selection);

            const { betSlip, ticketCount, isAutoRefresh, isUpdatingTicket, openFrom } = storeToRefs(useBetBuilderBetSlipStore());
            const { addTicket, isSameMatch, setOpenFrom } = useBetBuilderBetSlipStore();
            const { isShowBetBuilderBetSlip } = storeToRefs(useBetSlipStore());
            const { resetSingleModeForBetBuilder } = useSingleModeBetSlipStore();

            const name = computed(() => (isDisplayShortName.value ? shortName.value : fullName.value));

            const availableSelection = computed(() => betSlip.value.availableSelectionMap.get(selection.value.id));
            const isSelected = computed(() => {
                if (!isShowBetBuilderBetSlip.value) {
                    return false;
                }
                return betSlip.value.tickets.some(ticket => ticket.selection.id === selection.value.id);
            });
            const price = computed(() => availableSelection.value?.price ?? null);
            const isAvailable = computed(() => {
                if (!isShowBetBuilderBetSlip.value) {
                    return true;
                }
                return (ticketCount.value === 0 || !!availableSelection.value || !isSameMatch(match.value.eventId));
            });
            const displayPrice = computed(() => formatMinDecimalPlace(price.value?.value ?? 0));
            const isShowPrice = computed(() => !isSuspended.value && !isSelected.value && isAvailable.value && price.value !== null);

            const { isAsi, marketPage } = storeToRefs(useRootStore());

            const isSelectionLoading = computed(() => !isAutoRefresh.value && isUpdatingTicket.value);

            const { betPage } = useBetPage(marketPage.value, displaySection.value);
            const clickSelection = async () => {
                if (isSelectionLoading.value) {
                    return;
                }

                if (!isAsi.value) {
                    platform.showLogin('Sports');
                    return;
                }

                if (isSuspended.value || (!isAvailable.value && !isSelected.value)) {
                    return;
                }
                resetSingleModeForBetBuilder();
                await addTicket(selection.value, match.value, market.value);
                if (openFrom.value === BetPageType.Unknown) {
                    setOpenFrom(betPage.value);
                }
            };

            const { isPriceRise, isPriceDrop } = usePriceChange(price);

            return {
                isSuspended,
                isSelected,
                isAvailable,
                clickSelection,
                point,
                displayPrice,
                isShowPrice,
                isPriceRise,
                isPriceDrop,
                name,
                isSelectionLoading,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/selection";
</style>
